import type {
  DecisaoProjecaoTransferencia,
  EscopoDaRevisaoTipo,
  PreviaEscopoTransferenciaTipo,
} from "@/modules/financeiro-obras";
import { NO_VALUE } from "../../planejamento-controle.constants";
import { diasDoPeriodo, formatarData, paraData } from "../../planejamento-controle.datas";
import { resumoDaDecisao } from "../../projecao-transferencia";
import {
  SITUACAO_APTO,
  SITUACAO_IMPEDIDO,
  SITUACAO_PENDENCIA_TEMPORAL,
} from "./revisao-transferencia-tipo.constants";
import type { LinhaEscopoRevisao, TotalRevisao } from "./types-revisao-transferencia-tipo";

const MILHAO = 1_000_000;

export function formatMilhoesTotal(valor: number | null | undefined): string {
  return typeof valor === "number"
    ? (valor / MILHAO).toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : NO_VALUE;
}

const dataOuTraco = (iso: string) => {
  const data = paraData(iso);
  return data ? formatarData(data) : NO_VALUE;
};

export function periodoNoDestino(
  { dataInicio, dataFim }: { dataInicio: string; dataFim: string },
  formato: "coluna" | "cartao" = "coluna",
) {
  const inicio = paraData(dataInicio);
  const fim = paraData(dataFim);
  if (!inicio || !fim) return NO_VALUE;
  const datas = `${formatarData(inicio)} a ${formatarData(fim)}`;
  const dias = `${diasDoPeriodo(inicio, fim)} dias`;
  return formato === "coluna" ? `${datas} · ${dias}` : `${datas} (${dias})`;
}

const semMaiusculas = (id: string) => id.toLowerCase();

const totalDoEscopo = (
  escopo: EscopoDaRevisaoTipo,
  previa: PreviaEscopoTransferenciaTipo | undefined,
) => (previa ? previa.projecao?.totalDestino : escopo.valorDestino);

export interface ContextoLinhas {
  avisoDuracao: (fim: string) => string;
  decisoes: Record<string, DecisaoProjecaoTransferencia>;
  previas: PreviaEscopoTransferenciaTipo[];
  carregandoRevisarId: string | null;
  revisar: (id: string) => void;
}

export function linhasDaRevisao(
  escopos: EscopoDaRevisaoTipo[],
  { avisoDuracao, decisoes, previas, carregandoRevisarId, revisar }: ContextoLinhas,
): LinhaEscopoRevisao[] {
  const previaPorId = new Map(
    previas.map((previa) => [semMaiusculas(previa.obraEscopoMacroId), previa]),
  );
  return escopos.map((escopo) => {
    const id = escopo.obraEscopoMacroId;
    const impedido = escopo.impedimentos.length > 0;
    return {
      id,
      nome: escopo.nome,
      situacao: impedido
        ? { texto: SITUACAO_IMPEDIDO, severity: "danger" }
        : escopo.pendenciaDuracao
          ? { texto: SITUACAO_PENDENCIA_TEMPORAL, severity: "warning" }
          : { texto: SITUACAO_APTO, severity: "success" },
      periodo: periodoNoDestino(escopo.periodoDestino),
      classificacao: escopo.classificacao ?? NO_VALUE,
      parametros: resumoDaDecisao(decisoes[id]),
      total: formatMilhoesTotal(totalDoEscopo(escopo, previaPorId.get(semMaiusculas(id)))),
      pendenciaDuracao: escopo.pendenciaDuracao
        ? avisoDuracao(dataOuTraco(escopo.periodoDestino.dataFim))
        : null,
      impedimentos: escopo.impedimentos.map((impedimento) => impedimento.motivo),
      revisar: { carregando: carregandoRevisarId === id, onClick: () => revisar(id) },
      testIds: {
        linha: `transferir-tipo-escopo-${id}`,
        impedimentos: `transferir-tipo-impedimentos-${id}`,
        duracao: `transferir-tipo-duracao-${id}`,
        revisar: `transferir-tipo-revisar-${id}`,
      },
    };
  });
}

export function totalDaRevisao(
  escopos: EscopoDaRevisaoTipo[],
  previas: PreviaEscopoTransferenciaTipo[],
): TotalRevisao {
  const previaPorId = new Map(
    previas.map((previa) => [semMaiusculas(previa.obraEscopoMacroId), previa]),
  );
  const valores = escopos
    .map((escopo) =>
      totalDoEscopo(escopo, previaPorId.get(semMaiusculas(escopo.obraEscopoMacroId))),
    )
    .filter((valor) => typeof valor === "number");
  return {
    total:
      valores.length > 0
        ? formatMilhoesTotal(valores.reduce((soma, valor) => soma + valor, 0))
        : NO_VALUE,
  };
}
